import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Add from '@mui/icons-material/Add';
import { Button } from '@/ui';
import api from '../../../../services';
import {
  CriteriaPaneProps,
  CriteriaState,
  QualityScoreBypass,
  QualityScoreCriteria,
  QualityScoreRange,
} from '../types';
import {
  CriteriaSection,
  EmptyHint,
  EntryCard,
  FieldGrid,
  FieldLabel,
  FullRow,
  Input,
  MultiSelect,
  ReadOnlyValue,
  SelectSimple,
} from '../FormComponents';
import { FailureProbabilityInput } from '../../FailureProbabilityInput';
import { qualityScoreDayFilterOptions } from '../constants';
import { useAffiliateIdOptions } from '../useCriteriaOptions';
import { HELP } from '../criteriaHelp';
import type { CriteriaSectionDef } from './types';

const DEFAULT_CAP_RESET: QualityScoreCriteria['cap_reset'] = {
  resetInterval: 'daily',
  resetDayOfWeek: '',
  resetDayOfMonth: '',
  resetDate: '',
};

const rangesOf = (qs: QualityScoreCriteria | undefined): QualityScoreRange[] =>
  Array.isArray(qs?.qualityScoreRange) ? qs!.qualityScoreRange : [];
const bypassesOf = (qs: QualityScoreCriteria | undefined): QualityScoreBypass[] =>
  Array.isArray(qs?.bypass) ? qs!.bypass : [];

/** Next free `index_N` name — the name is the row's key in Redis, so it is never reused. */
const nextIndexName = (rows: { name?: string }[]) => {
  const max = Math.max(-1, ...rows.map((r) => Number(r.name?.match(/index_(\d+)/)?.[1] ?? -1)));
  return `index_${max + 1}`;
};

const toInt = (value: string) => (value ? parseInt(value, 10) : null);
const isInvalidProbability = (p: number | null | undefined) => p !== null && p !== undefined && (p < 0 || p >= 1);

type CurrentCaps = { quality_score_range?: Record<string, number>; bypass?: Record<string, number> };

/** Live per-row cap counters for the offer being edited. Shared by the ranges and bypass sections. */
function useCurrentQualityScoreCaps(): CurrentCaps | undefined {
  const offerId = useSelector(
    (s: any) =>
      s.offerState?.id || s.form?.offerBlockDetails?.values?.offer?.id || s.offerBlock?.curOfferBlock?.offer?.id
  );
  const { data } = useQuery({
    queryKey: ['affiliateQualityScoreCurrentCaps', offerId],
    queryFn: async () => (await api.get(`redis/affiliate-quality-score-current-caps/${offerId}?type=offer`)).data,
    enabled: Boolean(offerId),
  });
  return data;
}

function ProbabilityField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: number | null;
  onChange: (value: number) => void;
}) {
  const invalid = isInvalidProbability(value);
  return (
    // The input floats its error below itself; the padding keeps it off the next grid row.
    <Box sx={{ pb: invalid ? 2.5 : 0 }}>
      <FieldLabel htmlFor={id} help={HELP.failureProbability}>
        Failure Probability (%)
      </FieldLabel>
      <FailureProbabilityInput
        id={id}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value ? parseFloat(e.currentTarget.value) : 0)}
        placeholder='0%'
        min={0}
        max={0.99}
        step='0.01'
        isInvalid={invalid}
        errorMessage={invalid ? 'Must be between 0.00 and 0.99' : null}
      />
    </Box>
  );
}

const updateQualityScore = (
  setState: CriteriaPaneProps['setState'],
  update: (qs: QualityScoreCriteria) => Partial<QualityScoreCriteria>
) => setState((s) => ({ ...s, qualityScore: { ...s.qualityScore, ...update(s.qualityScore) } }));

/* ---------------------------------------------------------------------------------- Ranges */

function QualityScoreRangesSection({ state, setState }: CriteriaPaneProps) {
  const currentCaps = useCurrentQualityScoreCaps();
  const ranges = rangesOf(state.qualityScore);

  // Older configurations held a single min/max score. Turn it into the first range row, the
  // way clicking "Add Range" would, so it can be edited here.
  useEffect(() => {
    if (ranges.length > 0) return;
    const legacy = state as CriteriaState & { minScore?: string; maxScore?: string };
    const legacyMin = state.qualityScore?.minScore ?? legacy.minScore ?? null;
    const legacyMax = state.qualityScore?.maxScore ?? legacy.maxScore ?? null;
    if (legacyMin === null && legacyMax === null) return;
    const migrated: QualityScoreRange = {
      name: 'index_0',
      minScore: legacyMin as unknown as number,
      maxScore: legacyMax as unknown as number,
      cap: null,
      failureProbability: 0,
      validMultipliers: 0,
      dayFilters: [],
    };
    updateQualityScore(setState, (qs) => ({
      qualityScoreRange: [migrated],
      bypass: bypassesOf(qs),
      cap_reset: qs?.cap_reset || DEFAULT_CAP_RESET,
    }));
    // Only when the incoming quality score changes (initialisation), not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.qualityScore]);

  const addRange = () =>
    updateQualityScore(setState, (qs) => {
      const list = rangesOf(qs);
      return {
        qualityScoreRange: [
          ...list,
          {
            name: nextIndexName(list),
            minScore: null,
            maxScore: null,
            cap: null,
            failureProbability: 0,
            validMultipliers: 1,
            dayFilters: [],
          },
        ],
      };
    });
  const updateRange = (index: number, patch: Partial<QualityScoreRange>) =>
    updateQualityScore(setState, (qs) => ({
      qualityScoreRange: rangesOf(qs).map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
  const removeRange = (index: number) =>
    updateQualityScore(setState, (qs) => ({ qualityScoreRange: rangesOf(qs).filter((_, i) => i !== index) }));

  return (
    <CriteriaSection
      title='Affiliate Quality Score Ranges'
      help={HELP.qualityScoreRanges}
      actions={
        <Button color='primary' startIcon={<Add />} onClick={addRange}>
          Add Range
        </Button>
      }
    >
      {ranges.length === 0 && <EmptyHint>No ranges. Traffic is not limited by affiliate quality score.</EmptyHint>}
      {ranges.map((range, index) => {
        const name = range.name || `index_${index}`;
        const redisIndex = range.name?.replace('index_', '') || String(index);
        const invalidMultiplier =
          range.validMultipliers === null || range.validMultipliers === undefined || range.validMultipliers < 0;

        return (
          <EntryCard
            key={range.name ?? index}
            title={name}
            onRemove={() => removeRange(index)}
            removeLabel='Remove this range'
          >
            <FieldGrid min='9rem'>
              <Input
                label='Min Score'
                type='number'
                value={range.minScore?.toString() ?? ''}
                onChange={(v) => updateRange(index, { minScore: toInt(v) })}
                placeholder='Min Score'
                help={HELP.qualityScoreMinMax}
              />
              <Input
                label='Max Score'
                type='number'
                value={range.maxScore?.toString() ?? ''}
                onChange={(v) => updateRange(index, { maxScore: toInt(v) })}
                placeholder='Max Score'
                help={HELP.qualityScoreMinMax}
              />
              <Input
                label='Cap Amount'
                type='number'
                min={0}
                value={range.cap?.toString() ?? ''}
                onChange={(v) => updateRange(index, { cap: toInt(v) })}
                placeholder='Infinity'
                help={HELP.qualityScoreCap}
              />
              <ReadOnlyValue label='Current Cap' value={currentCaps?.quality_score_range?.[redisIndex] || 0} />
              <ProbabilityField
                id={`quality-score-range-${index}-failure`}
                value={range.failureProbability}
                onChange={(v) => updateRange(index, { failureProbability: v })}
              />
              <Input
                label='Min Valid Multipliers'
                type='number'
                min={0}
                value={range.validMultipliers?.toString() ?? ''}
                onChange={(v) => updateRange(index, { validMultipliers: toInt(v) })}
                placeholder='0'
                error={invalidMultiplier ? 'Must be 0 or greater' : undefined}
                help={HELP.validMultipliers}
              />
              <FullRow>
                <MultiSelect
                  label='Day Filter'
                  value={range.dayFilters || []}
                  onChange={(v) => updateRange(index, { dayFilters: v })}
                  options={qualityScoreDayFilterOptions}
                  placeholder='All Days'
                  help={HELP.dayFilter}
                />
              </FullRow>
            </FieldGrid>
          </EntryCard>
        );
      })}
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------------- Bypass Affiliates */

function BypassAffiliatesSection({ state, setState }: CriteriaPaneProps) {
  const currentCaps = useCurrentQualityScoreCaps();
  const { options: affiliateOptions, isLoading } = useAffiliateIdOptions();
  const bypasses = bypassesOf(state.qualityScore);

  const addBypass = () =>
    updateQualityScore(setState, (qs) => {
      const list = bypassesOf(qs);
      return {
        bypass: [...list, { name: nextIndexName(list), bypass_affiliate: [], cap: null, failureProbability: 0 }],
      };
    });
  const updateBypass = (index: number, patch: Partial<QualityScoreBypass>) =>
    updateQualityScore(setState, (qs) => ({
      bypass: bypassesOf(qs).map((b, i) => (i === index ? { ...b, ...patch } : b)),
    }));
  const removeBypass = (index: number) =>
    updateQualityScore(setState, (qs) => ({ bypass: bypassesOf(qs).filter((_, i) => i !== index) }));

  return (
    <CriteriaSection
      title='Bypass Affiliates'
      help={HELP.bypassAffiliates}
      actions={
        <Button color='primary' startIcon={<Add />} onClick={addBypass}>
          Add Bypass
        </Button>
      }
    >
      {bypasses.length === 0 && <EmptyHint>No bypass rows.</EmptyHint>}
      {bypasses.map((bypass, index) => {
        const redisIndex = bypass.name?.replace('index_', '') || String(index);
        return (
          <EntryCard
            key={bypass.name ?? index}
            title={bypass.name || `index_${index}`}
            onRemove={() => removeBypass(index)}
            removeLabel='Remove this bypass'
          >
            <FieldGrid min='9rem'>
              <FullRow>
                <MultiSelect
                  label='Affiliates (IDs)'
                  value={bypass.bypass_affiliate.map(String)}
                  onChange={(selected) =>
                    updateBypass(index, { bypass_affiliate: selected.map((s) => parseInt(s, 10)) })
                  }
                  options={affiliateOptions}
                  isLoading={isLoading}
                />
              </FullRow>
              <Input
                label='Cap Amount'
                type='number'
                min={0}
                value={bypass.cap?.toString() ?? ''}
                onChange={(v) => updateBypass(index, { cap: toInt(v) })}
                placeholder='Infinity'
                help={HELP.qualityScoreCap}
              />
              <ReadOnlyValue label='Current Cap' value={currentCaps?.bypass?.[redisIndex] || 0} />
              <ProbabilityField
                id={`bypass-${index}-failure`}
                value={bypass.failureProbability}
                onChange={(v) => updateBypass(index, { failureProbability: v })}
              />
            </FieldGrid>
          </EntryCard>
        );
      })}
    </CriteriaSection>
  );
}

/* ---------------------------------------------------------------------------------- Cap Reset */

/** Only a daily reset is offered, so the day/date fields stay inert until other intervals exist. */
function CapResetSection({ state, setState }: CriteriaPaneProps) {
  const capReset = state.qualityScore?.cap_reset || DEFAULT_CAP_RESET;
  const setReset = (patch: Partial<QualityScoreCriteria['cap_reset']>) =>
    updateQualityScore(setState, (qs) => ({ cap_reset: { ...(qs.cap_reset || DEFAULT_CAP_RESET), ...patch } }));
  const notApplicable = [{ label: 'Not applicable for daily reset', value: '' }];

  return (
    <CriteriaSection title='Cap Reset Configuration' help={HELP.capReset}>
      <FieldGrid min='12rem'>
        <SelectSimple
          label='Reset Interval'
          value={capReset.resetInterval || 'daily'}
          onChange={(v) => setReset({ resetInterval: v || 'daily' })}
          options={[{ label: 'Daily', value: 'daily' }]}
          placeholder='Daily'
        />
        <SelectSimple
          label='Reset Day of Week'
          value={capReset.resetDayOfWeek || ''}
          onChange={(v) => setReset({ resetDayOfWeek: v })}
          options={notApplicable}
          placeholder='Not applicable for daily reset'
          disabled
        />
        <SelectSimple
          label='Reset Day of Month'
          value={capReset.resetDayOfMonth || ''}
          onChange={(v) => setReset({ resetDayOfMonth: v })}
          options={notApplicable}
          placeholder='Not applicable for daily reset'
          disabled
        />
        <Input
          label='Reset Date'
          value={capReset.resetDate || ''}
          onChange={(v) => setReset({ resetDate: v })}
          placeholder='Not applicable for daily reset'
          disabled
        />
      </FieldGrid>
    </CriteriaSection>
  );
}

/* ---------------------------------------------------------------------------------- Registry */

export const QUALITY_SCORE_SECTIONS: CriteriaSectionDef[] = [
  {
    id: 'qualityScoreRanges',
    tab: 'affiliateQualityScore',
    title: 'Affiliate Quality Score Ranges',
    fields: ['Add Range'],
    aliases: [
      'Min Score',
      'Max Score',
      'Cap Amount',
      'Failure Probability (%)',
      'Min Valid Multipliers',
      'Day Filter',
      'quality score',
      'aqs',
      'score',
    ],
    stateKeys: ['qualityScore'],
    isConfigured: (s) => rangesOf(s.qualityScore).length > 0,
    Component: QualityScoreRangesSection,
  },
  {
    id: 'bypassAffiliates',
    tab: 'affiliateQualityScore',
    title: 'Bypass Affiliates',
    fields: ['Add Bypass'],
    aliases: ['Affiliates (IDs)', 'bypass', 'quality score', 'aqs'],
    stateKeys: ['qualityScore'],
    isConfigured: (s) => bypassesOf(s.qualityScore).length > 0,
    Component: BypassAffiliatesSection,
  },
  {
    id: 'capReset',
    tab: 'affiliateQualityScore',
    title: 'Cap Reset Configuration',
    fields: ['Reset Interval', 'Reset Day of Week', 'Reset Day of Month', 'Reset Date'],
    aliases: ['reset', 'quality score'],
    stateKeys: ['qualityScore'],
    // Daily is the default and the only interval offered, so this never holds a choice of its own.
    isConfigured: () => false,
    Component: CapResetSection,
  },
];
